import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { localSort } from 'app/shared/lib/sorting';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';

export interface CustomCase extends CM.Case {
    modifiedBy?: Core.User | null;
}

@autoinject()
export class CurrentCaseStatusesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    protected cases: ReadonlyArray<CustomCase> = [];

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;

    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['modifiedBy.description'];
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();

        // Default Sort
        this.apiSorting.entries = [
            {
                reference: 'member.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const cases: CustomCase[] = <CustomCase[]>this.cases;
            if(direction === null) {
                localSorting.remove(property);
                this.reload();
            } else {
                localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(cases, localSorting);
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCasesWithStatusHistory();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    // Gives each case its modifiedBy property
    private restructureCases(): void {
        if(this.cases.length == 0) return;

        this.cases.forEach(memberCase => {
            if(!memberCase.history?.statuses) return;
            memberCase.modifiedBy = memberCase.history?.statuses[0].metadata?.started?.byUser || {};;
        });
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCasesWithStatusHistory(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.apiSorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes: string[] = [
            'member',
            'status',
            'status.reason',
            'history.statuses',
            'history.statuses.metadata.started.byUser'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCasesByHouseholdCase(this.householdCaseId, options, includes);

            this.cases = response.results;
            this.restructureCases();
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
